import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Deals Stack — draggable card stack.
 * The front card follows the pointer; flung past a threshold it flies off and is sent to the back.
 * A periodic "nudge" hints that the top card is draggable and stops on first interaction.
 * Pointer events + CSS keyframes → runs in every browser.
 */
class DealsStackComponent extends Component {
  requiredRefs = ['stack'];

  connectedCallback() {
    super.connectedCallback();
    this.cards = Array.from(this.querySelectorAll('.ds__card'));
    if (this.cards.length === 0) return;
    this.order = this.cards.slice();
    this.#layout();
    this.#bindTop();
    this.#startNudge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this._nudgeTimer);
    this._nudgeIO?.disconnect();
  }

  #layout() {
    const n = this.order.length;
    this.order.forEach((card, i) => {
      const depth = Math.min(i, 3);
      card.style.setProperty('--depth', depth);
      card.style.setProperty('--dx', '0px');
      card.style.setProperty('--rot', '0deg');
      card.style.setProperty('--fade', i > 3 ? '0' : '1');
      card.style.zIndex = String(n - i);
      const isTop = i === 0;
      card.classList.toggle('ds__card--top', isTop);
      card.style.pointerEvents = isTop ? 'auto' : 'none';
      card.setAttribute('aria-hidden', isTop ? 'false' : 'true');
    });
  }

  #bindTop() {
    const top = this.order[0];
    if (top) top.addEventListener('pointerdown', this.#down);
  }

  #down = (e) => {
    // Let links/buttons work normally — don't start a drag from the CTA.
    if (e.target.closest('a, button')) return;
    const card = e.currentTarget;
    this._start = { x: e.clientX, card };
    try { card.setPointerCapture(e.pointerId); } catch (_) {}
    card.classList.add('is-grabbing');
    card.addEventListener('pointermove', this.#move);
    card.addEventListener('pointerup', this.#up, { once: true });
    card.addEventListener('pointercancel', this.#up, { once: true });
  };

  #move = (e) => {
    if (!this._start) return;
    const dx = e.clientX - this._start.x;
    const card = this._start.card;
    card.style.setProperty('--dx', dx + 'px');
    card.style.setProperty('--rot', (dx / 22) + 'deg');
    card.style.setProperty('--fade', Math.max(0.2, 1 - Math.abs(dx) / 480).toFixed(3));
  };

  #up = (e) => {
    if (!this._start) return;
    const dx = e.clientX - this._start.x;
    const card = this._start.card;
    card.classList.remove('is-grabbing');
    card.removeEventListener('pointermove', this.#move);
    if (Math.abs(dx) > 120) {
      this.#sendToBack(card, dx > 0 ? 1 : -1);
    } else {
      card.style.setProperty('--dx', '0px');
      card.style.setProperty('--rot', '0deg');
      card.style.setProperty('--fade', '1');
    }
    this._start = null;
  };

  #sendToBack(card, dir) {
    card.removeEventListener('pointerdown', this.#down);
    card.style.transition = 'transform .35s ease, opacity .35s ease';
    card.style.setProperty('--dx', (dir * 640) + 'px');
    card.style.setProperty('--rot', (dir * 18) + 'deg');
    card.style.setProperty('--fade', '0');
    const settle = () => {
      card.removeEventListener('transitionend', settle);
      // Move to back of order, drop transition so the reset is instant, then relayout.
      this.order = this.order.slice(1).concat(card);
      card.style.transition = 'none';
      this.#layout();
      // Force reflow so the removed transition takes effect before re-enabling it.
      void card.offsetWidth;
      card.style.transition = '';
      this.#bindTop();
    };
    card.addEventListener('transitionend', settle, { once: true });
    // Safety: if transitionend never fires, settle anyway.
    setTimeout(() => { if (this.order[0] === card) settle(); }, 500);
  }

  #startNudge() {
    if (prefersReducedMotion()) return;
    const play = () => {
      const top = this.order[0];
      if (!top) return;
      top.classList.remove('nudge');
      void top.offsetWidth;
      top.classList.add('nudge');
    };
    if ('IntersectionObserver' in window) {
      this._nudgeIO = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) { play(); this._nudgeIO.disconnect(); }
      });
      this._nudgeIO.observe(this);
    }
    this._nudgeTimer = setInterval(play, 4200);
    this.addEventListener('pointerdown', () => {
      clearInterval(this._nudgeTimer);
      this.order[0]?.classList.remove('nudge');
    }, { once: true });
  }
}

if (!customElements.get('deals-stack-component')) {
  customElements.define('deals-stack-component', DealsStackComponent);
}
